import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Button, Dropdown, Icon } from "semantic-ui-react";
import styles from "./Filters.module.css";
import { filterTypes, filterTypesParser } from "./filterTypes/filterTypes";
import ClearButton from "./ClearButton";
import {
  getHashParameterIndex,
  getHashParameters,
  removeHashParameter,
  removeHashParameters,
  setHashParameter
} from "../../utils/hashToObject";

export const parseFilters = (options, location) => {
  const params = getHashParameters(location);
  return options
    .filter(option => params[option.id])
    .reduce((arr, option) => {
      const parser = option.parser || filterTypesParser[option.type];
      const parser1 = parser(option, params[option.id]);
      return [...arr, ...parser1];
    }, []);
};

const Filters = ({
  filterName,
  data,
  options,
  disabled,
  location,
  tag,
  additionalButtons,
  disableChecker,
  navigate
}) => {
  const optionsMap = useMemo(() => {
    return options.reduce((map, option) => {
      map[option.id] = option;
      return map;
    }, {});
  }, [options]);

  const selectedFilters = useMemo(
    () =>
      options
        .map(option => ({
          option,
          index: getHashParameterIndex(location, option.id, tag)
        }))
        .filter(option => Number.isInteger(option.index))
        .sort((o1, o2) => o1.index - o2.index)
        .map(option => option.option.id),
    [options, location]
  );

  const notSelectedOptions = useMemo(
    () =>
      options
        .filter(
          option =>
            selectedFilters.findIndex(filter => filter === option.id) === -1
        )
        .map(option => ({
          key: option.id,
          text: option.name,
          value: option.id,
          label: { icon: "plus" }
        })),
    [options, selectedFilters]
  );

  const onChange = useCallback(
    (_, data) => {
      setHashParameter(location, data.value, "*", tag, navigate);
    },
    [location]
  );
  const defaultSeason = data.seasons?.reduce(function(prev, current) {
    return prev.startAt > current.startAt ? prev : current;
  }, {});
  const defaultDate = [defaultSeason?.startAt, defaultSeason?.endAt];

  useEffect(() => {
    defaultSeason?.startAt &&
      defaultSeason?.endAt &&
      setHashParameter(location, "seasons", defaultDate, null, navigate);
  }, []);

  const clearFilters = useCallback(() => {
    removeHashParameters(
      location,
      options.map(option => option.id),
      tag,
      navigate
    );
  }, [options, location]);

  const removeFilter = useCallback(
    id => {
      removeHashParameter(location, id, tag, navigate);
    },
    [location]
  );

  const getFilter = useCallback(
    id => {
      const filter = optionsMap[id];
      const FilterComponent = filterTypes[filter.type];
      return (
        <div key={id} className={styles.filterBox}>
          <FilterComponent
            // disabled={disabled}
            disableChecker={disableChecker}
            {...filter}
          />
          <Button
            icon="close"
            className="button-text"
            disabled={
              /* disabled || */
              (disableChecker === "date" && filter.name === "Season") ||
              (disableChecker === "seasons" &&
                (filter.name === "Expiry Date" || filter.name === "Date"))
            }
            onClick={() => removeFilter(id)}
          />
        </div>
      );
    },
    [disabled, optionsMap, removeFilter]
  );

  return (
    <div className={styles.tabsAndFilters}>
      <div className={styles.filtersHolder}>
        <div className={styles.filterAdd}>
          <Dropdown
            floating
            labeled
            button
            disabled={!notSelectedOptions.length}
            className={`icon ${styles.buttonFilters}`}
            search
            icon={
              <>
                <Icon name="sliders horizontal" />
                <span className={styles.countFilters}>
                  {selectedFilters.length}
                </span>
              </>
            }
            text="Add Filter"
            selection
            options={notSelectedOptions}
            value={null}
            onChange={onChange}
            selectOnBlur={false}
          />
        </div>
        {selectedFilters.length > 0 && (
          <ClearButton
            className="hide-md-inline-block"
            onClick={clearFilters}
          />
        )}
        {selectedFilters.length > 0 && (
          <span className={styles.filtersItems}>
            {selectedFilters.map(getFilter)}
          </span>
        )}
        {additionalButtons.length > 0 &&
          additionalButtons.map(({ clickHandler, text }, index) => (
            <Button
              key={`${text}_${index}`}
              className={styles.additionalButton}
              onClick={clickHandler}
            >
              {text}
            </Button>
          ))}
        {selectedFilters.length > 0 && (
          <ClearButton
            className="show-md-inline-block"
            onClick={clearFilters}
          />
        )}
      </div>
    </div>
  );
};

Filters.propTypes = {
  disabled: PropTypes.bool,
  location: PropTypes.object,
  options: PropTypes.array.isRequired,
  additionalButtons: PropTypes.array,
  tag: PropTypes.string
};

Filters.defaultProps = {
  tag: null,
  additionalButtons: []
};

function mapStateToProps(state) {
  const {
    settings: { data }
  } = state;
  return {
    data
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
